export const DEFAULT_VASI_BACKEND_URL = 'https://vasi-backend.myhemispheresneuro.org';

export const TANNER_GENDERS = ['M', 'F'];
export const TANNER_STAGES = [1, 2, 3, 4, 5];

// Uploads block the serial batch, so cap each request; the backend runs
// MediaPipe extraction before answering, which can take a while.
export const UPLOAD_TIMEOUT_MS = 120000;

// Local dev: set REACT_APP_VASI_BACKEND_URL=http://localhost:8000 AND start the
// backend with VASI_CORS_ORIGINS including the SPA origin (e.g.
// http://localhost:3000), or the browser will block the POST /stimuli call.
export function stimuliBackendBaseUrl(env) {
  const configured = env
    ? env.REACT_APP_VASI_BACKEND_URL
    : process.env.REACT_APP_VASI_BACKEND_URL;
  const base = (configured || '').trim() || DEFAULT_VASI_BACKEND_URL;
  return base.replace(/\/+$/, '');
}

export function emptyStimulusMetadata() {
  return {
    type: 'tanner',
    gender: '',
    tanner: '',
    control: '',
    controlGroup: '',
  };
}

export function validateStimulusMetadata(metadata) {
  const errors = [];
  const type = metadata?.type;

  if (type !== 'tanner' && type !== 'control') {
    errors.push('Select a stimulus type.');
    return errors;
  }

  if (type === 'tanner') {
    if (!TANNER_GENDERS.includes(metadata.gender)) {
      errors.push('Select a gender (M or F).');
    }
    if (!TANNER_STAGES.includes(Number(metadata.tanner))) {
      errors.push('Select a Tanner stage (1-5).');
    }
    return errors;
  }

  if (!/^\d+$/.test(String(metadata.control ?? '').trim())) {
    errors.push('Enter a control number.');
  }
  if (!/^\d+$/.test(String(metadata.controlGroup ?? '').trim())) {
    errors.push('Enter a control group number.');
  }
  return errors;
}

export function buildStimulusFormData(file, metadata) {
  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('type', metadata.type);

  if (metadata.type === 'tanner') {
    formData.append('gender', metadata.gender);
    formData.append('tanner', String(Number(metadata.tanner)));
  } else {
    formData.append('control', String(metadata.control).trim());
    formData.append('control_group', String(Number(metadata.controlGroup)));
  }

  return formData;
}

// Applies a metadata patch to a wizard item. An already-settled (uploaded or
// failed) item whose metadata actually changes goes back to 'pending' so the
// upload step re-sends it — the backend writes with merge=True, so a re-POST
// of the same file and type lands on the same doc and is safe. The TYPE of an
// item that has uploaded (item.hash set) is locked: the type drives the
// server-side transformation, so changing it re-hashes to a NEW doc and
// orphans the old one. Changing type requires remove (which deletes the
// uploaded record) + re-add. A no-op patch returns the item unchanged.
export function applyMetadataPatch(item, patch) {
  let effective = patch;
  if (item.hash && patch.type && patch.type !== item.metadata.type) {
    const { type, ...rest } = patch;
    effective = rest;
  }
  const metadata = { ...item.metadata, ...effective };
  const changed = Object.keys(metadata)
    .some((key) => metadata[key] !== item.metadata[key]);
  if (!changed) {
    return item;
  }
  const next = { ...item, metadata };
  if (item.status !== 'pending') {
    next.status = 'pending';
    next.progress = 0;
    next.error = null;
  }
  return next;
}

// Whether removing this uploaded item should also delete its backend record.
// Ownership lives in a BATCH-level set of hashes (not on the item): a hash
// enters the set the first time any item's POST /stimuli response reports
// created:true (that request created the images/{hash} doc, atomic via a
// Firestore create-precondition), and later created:false responses or twin
// removals never revoke it. A response without the flag (older backend)
// never establishes ownership — fail closed. A record another item in this
// batch still references must survive regardless; only removing the LAST
// item of a hash may delete. The actual delete is additionally guarded by a
// server-side ownership transaction in the wizard.
export function canDeleteUploadedRecord(item, items, ownedHashes) {
  return !!item.hash
    && !!ownedHashes
    && ownedHashes.has(item.hash)
    && !items.some((other) => other.id !== item.id && other.hash === item.hash);
}

export function summarizeBatch(items) {
  const summary = {
    total: items.length,
    uploaded: 0,
    failed: 0,
    pending: 0,
    tannerUploaded: 0,
    controlUploaded: 0,
    tannerFailed: 0,
    tannerTotal: 0,
  };

  for (const item of items) {
    const isTanner = item.metadata?.type !== 'control';
    if (isTanner) {
      summary.tannerTotal += 1;
    }
    if (item.status === 'success') {
      summary.uploaded += 1;
      if (isTanner) {
        summary.tannerUploaded += 1;
      } else {
        summary.controlUploaded += 1;
      }
    } else if (item.status === 'error') {
      summary.failed += 1;
      if (isTanner) {
        summary.tannerFailed += 1;
      }
    } else {
      summary.pending += 1;
    }
  }

  return summary;
}

export function uploadStimulus({ baseUrl, token, file, metadata, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}/stimuli`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.responseType = 'json';
    xhr.timeout = UPLOAD_TIMEOUT_MS;

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response || {});
      } else {
        const detail = xhr.response && typeof xhr.response === 'object'
          ? xhr.response.detail
          : null;
        reject(new Error(
          typeof detail === 'string' && detail
            ? detail
            : `Upload failed (HTTP ${xhr.status}).`,
        ));
      }
    };
    xhr.onerror = () => reject(new Error('Network error while uploading.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out.'));

    xhr.send(buildStimulusFormData(file, metadata));
  });
}
